/**
 * Inline panel for adding new sub-units to an order. Asks for a count and
 * a required suffix per unit (e.g. "U1", "APRONS", "LEFT"). Suffixes are
 * upper-cased and have whitespace converted to underscores so the
 * resulting unit_order_number stays scan/URL-safe. Every unit must have a
 * suffix entered by the user — there is no auto-assigned default.
 */

import React, { useState, useMemo } from 'react';
import { TextField } from '@mui/material';
import { Row, Col } from 'react-bootstrap';
import { MyDiv } from '../Pages/Common/Components';
import '../styles/UnitAddPanel.scss';

const MAX_UNITS = 20;
const MAX_SUFFIX_LENGTH = 8;

const UnitAddPanel = ({ orderUniqueId, startOrdinal = 1, onConfirm, onCancel }) => {
  const [count, setCount] = useState('');
  const [suffixes, setSuffixes] = useState([]);

  const safeCount = useMemo(() => {
    const n = Number(count);
    if (count === '' || !Number.isInteger(n) || n < 1) return 0;
    return Math.min(MAX_UNITS, n);
  }, [count]);

  // Every unit must have a non-blank suffix before the form can submit.
  const allSuffixesFilled = useMemo(
    () => safeCount > 0 && suffixes.slice(0, safeCount).every((s) => (s || '').trim() !== ''),
    [safeCount, suffixes]
  );

  const handleCountChange = (raw) => {
    setCount(raw);
    const n = Number(raw);
    if (raw === '' || !Number.isInteger(n) || n < 1) {
      setSuffixes([]);
      return;
    }
    const clamped = Math.min(MAX_UNITS, n);
    setSuffixes((prev) => {
      const next = prev.slice(0, clamped);
      while (next.length < clamped) next.push('');
      return next;
    });
  };

  const handleSuffixChange = (idx, value) => {
    setSuffixes((prev) => {
      const next = prev.slice();
      next[idx] = value;
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (safeCount === 0 || !allSuffixesFilled) return;
    const units = Array.from({ length: safeCount }, (_, i) => {
      const suffixRaw = (suffixes[i] || '').trim().toUpperCase().replace(/\s+/g, '_');
      return { unit_id: suffixRaw };
    });
    // Suffix is mandatory — bail if any came out blank after sanitizing.
    if (units.some((u) => !u.unit_id)) return;
    onConfirm(units);
  };

  return (
    <MyDiv className="unit-add-panel">
      <div className="unit-add-panel__header">
        <div>
          <h5 className="unit-add-panel__title">Add sub-units</h5>
          <p className="unit-add-panel__subtitle">
            Split order <strong>{orderUniqueId}</strong> into independently-tracked units.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Row className="g-3 align-items-start">
          <Col xs={12} md={3} lg={2}>
            <TextField
              label="Number of units"
              type="number"
              variant="outlined"
              size="small"
              value={count}
              onChange={(e) => handleCountChange(e.target.value)}
              inputProps={{ min: 1, max: MAX_UNITS }}
              fullWidth
              autoFocus
              placeholder="e.g. 3"
            />
          </Col>

          {safeCount > 0 && (
            <Col xs={12} md={9} lg={10}>
              <Row className="g-2">
                {Array.from({ length: safeCount }).map((_, i) => {
                  const defaultSuffix = `U${startOrdinal + i}`;
                  return (
                    <Col key={i} xs={6} sm={4} md={3} lg={2}>
                      <TextField
                        required
                        label={`Unit ${i + 1} suffix`}
                        variant="outlined"
                        size="small"
                        value={suffixes[i] || ''}
                        onChange={(e) => handleSuffixChange(i, e.target.value)}
                        fullWidth
                        placeholder={`e.g. ${defaultSuffix}`}
                        inputProps={{ maxLength: MAX_SUFFIX_LENGTH }}
                      />
                    </Col>
                  );
                })}
              </Row>
              <p className="unit-add-panel__hint">
                Every unit needs a suffix — there is no default. Suffixes are auto upper-cased, must be unique within the order, and capped at {MAX_SUFFIX_LENGTH} characters.
              </p>
            </Col>
          )}
        </Row>

        <div className="unit-add-panel__actions">
          <button
            type="button"
            className="unit-add-panel__btn unit-add-panel__btn--cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="unit-add-panel__btn unit-add-panel__btn--submit"
            disabled={safeCount === 0 || !allSuffixesFilled}
          >
            {safeCount === 0
              ? 'Enter a count'
              : !allSuffixesFilled
              ? 'Enter every suffix'
              : `Add ${safeCount} unit${safeCount === 1 ? '' : 's'}`}
          </button>
        </div>
      </form>
    </MyDiv>
  );
};

export default UnitAddPanel;
